/*
 * Description:
 * ---------------------
 * TAK Map Marker: whether this appliance publishes itself to a TAK server, and
 * what that marker says (cloud-anchor/pkg/tak).
 *
 * The appliance opens one outbound connection and publishes a Cursor on Target
 * event on a cadence. Any TAK client on the same server renders it with nothing
 * installed on their side. The panel answers "are we on the map" first, shows
 * the circuits the marker is reporting, and keeps the settings behind one
 * disclosure so the common visit is a glance. All wording lives in takWords.js.
 *
 * ! file components/TakPanel.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiMapPin, FiSend, FiSearch } from "react-icons/fi";
import useVisibleInterval from "../hooks/useVisibleInterval";
import useEdgeFeature, { FEATURE_ABSENT } from "../hooks/useEdgeFeature";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { fmtDuration } from "../utils/formatters";
import * as W from "./takWords";
import "./TakPanel.css";

const POLL_MS = 5000;

export default function TakPanel({ nodeApiUrl, selectedNode }) {
  if (!selectedNode) {
    return (
      <div className="panel-card tak-panel">
        <div className="tak-empty">
          <FiSearch aria-hidden="true" />
          <h2>Select an edge node</h2>
          <p>Choose a box to see whether it is on the TAK map.</p>
        </div>
      </div>
    );
  }
  // Keyed on the node so switching edges rebuilds rather than showing the
  // previous box's settings while the new one loads.
  return <TakMarker key={selectedNode} nodeApiUrl={nodeApiUrl} selectedNode={selectedNode} />;
}

function TakMarker({ nodeApiUrl, selectedNode }) {
  const [view, setView] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const mounted = useRef(true);
  const inflight = useRef(null);
  const { state: featureState } = useEdgeFeature(selectedNode, "/tak");

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      inflight.current?.abort();
      inflight.current = null;
    };
  }, []);

  const load = useCallback(async () => {
    const url = nodeApiUrl?.("/tak");
    if (!url) return;
    inflight.current?.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    const res = await fetchWithTimeout(url, { signal: ctrl.signal }).catch(() => null);
    if (!mounted.current || !res || !res.ok) return;
    const body = await res.json().catch(() => null);
    if (!mounted.current || !body) return;
    setView(body);
    // Do not overwrite a form the operator is part way through typing into.
    setForm((prev) => (prev && editing ? prev : body.config));
  }, [nodeApiUrl, editing]);

  useVisibleInterval(load, POLL_MS);

  if (featureState === FEATURE_ABSENT) {
    return (
      <div className="panel-card tak-panel">
        <FeatureUnavailableNotice nodeId={selectedNode} feature="TAK map marker" />
      </div>
    );
  }
  if (!view) {
    return (
      <div className="panel-card tak-panel">
        <p className="tak-loading">Reading this appliance…</p>
      </div>
    );
  }

  const { status } = view;
  const head = W.headline(status);

  const save = async (next) => {
    setSaving(true);
    setSaveError("");
    setNotice("");
    const url = nodeApiUrl?.("/tak/config");
    const res = await fetchWithTimeout(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(next),
    }).catch(() => null);
    if (!mounted.current) return;
    setSaving(false);
    if (!res) {
      setSaveError("Could not reach this appliance.");
      return;
    }
    if (!res.ok) {
      // The edge answers 400 with a sentence naming what to fix; show it rather
      // than a generic failure.
      setSaveError((await res.text().catch(() => "")) || "The appliance refused this configuration.");
      return;
    }
    const body = await res.json().catch(() => null);
    if (body) {
      setView(body);
      setForm(body.config);
    }
    setEditing(false);
  };

  const sendTest = async () => {
    setNotice("");
    setSaveError("");
    const url = nodeApiUrl?.("/tak/test");
    const res = await fetchWithTimeout(url, { method: "POST" }).catch(() => null);
    if (!mounted.current || !res) return;
    const body = await res.json().catch(() => null);
    if (!body) return;
    if (body.status) setView((v) => ({ ...v, status: body.status }));
    setNotice(body.published ? W.TEST_SENT : body.reason || "It could not publish.");
  };

  return (
    <div className="panel-card tak-panel">
      <div className={`tak-head tak-${head.tone}`}>
        {head.tone === "good" ? <FiCheckCircle aria-hidden="true" />
          : head.tone === "bad" ? <FiAlertTriangle aria-hidden="true" />
            : <FiMapPin aria-hidden="true" />}
        <div>
          <h2>{head.title}</h2>
          <p>{head.detail}</p>
        </div>
      </div>

      <dl className="tak-facts">
        <div><dt>Server</dt><dd>{status.server || "not set"}</dd></div>
        <div><dt>Callsign</dt><dd>{status.callsign || "not set"}</dd></div>
        <div><dt>Refreshed every</dt><dd>{fmtDuration(status.interval_seconds)}</dd></div>
        <div><dt>Markers sent</dt><dd>{status.published || 0}</dd></div>
      </dl>

      {!status.location_set && <p className="tak-note">{W.POSITION_UNSET}</p>}
      {notice && <p className="tak-notice" role="status">{notice}</p>}

      <Circuits links={view.links} status={status} />

      <div className="tak-actions">
        <button type="button" className="tak-btn" onClick={() => { setEditing((e) => !e); setSaveError(""); }}>
          {editing ? "Close settings" : "Settings"}
        </button>
        <button type="button" className="tak-btn" onClick={sendTest} disabled={!status.enabled}>
          <FiSend aria-hidden="true" /> Send a test event
        </button>
      </div>

      {editing && form && (
        <SettingsForm
          form={form} setForm={setForm} onSave={save}
          saving={saving} error={saveError}
        />
      )}
    </div>
  );
}

/* What the marker is reporting about each circuit right now. The edge sends
   these with the status so the panel shows what went on the wire rather than
   re-deriving it from another route and risking a different answer. */
function Circuits({ links, status }) {
  if (!status.enabled) return null;
  if (!links || !links.length) {
    return <p className="tak-note">This appliance reported no circuits.</p>;
  }
  return (
    <ul className="tak-circuits">
      {links.map((l) => (
        <li key={l.name} className={`tak-circuit tak-${W.circuitTone(l)}`}>
          <span className="tak-circuit-name">{l.label || l.name}</span>
          <span className="tak-circuit-state">{W.circuitLine(l)}</span>
        </li>
      ))}
    </ul>
  );
}

function SettingsForm({ form, setForm, onSave, saving, error }) {
  const set = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };
  const num = (v) => (String(v).trim() === "" ? 0 : Number(v));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      port: num(form.port),
      interval_seconds: num(form.interval_seconds),
      lat: num(form.lat),
      lon: num(form.lon),
    });
  };

  return (
    <form className="tak-form" onSubmit={submit}>
      <label className="tak-toggle">
        <input type="checkbox" checked={!!form.enabled} onChange={set("enabled")} />
        <span>Publish this appliance to a TAK server</span>
      </label>

      <div className="tak-grid">
        <Field label="Server address" help={W.FIELD_HELP.host}>
          <input className="tak-input" value={form.host || ""} onChange={set("host")}
            placeholder="tak.example.mil" aria-label="TAK server address" />
        </Field>
        <Field label="Port" help={W.FIELD_HELP.port}>
          <input className="tak-input" type="number" min="1" max="65535"
            value={form.port ?? ""} onChange={set("port")} aria-label="TAK server port" />
        </Field>
        <Field label="Callsign" help={W.FIELD_HELP.callsign}>
          <input className="tak-input" value={form.callsign || ""} onChange={set("callsign")}
            placeholder="WEAVIAN-01" aria-label="Callsign" />
        </Field>
        <Field label="Refresh every" help={W.FIELD_HELP.interval} unit="seconds">
          <input className="tak-input" type="number" min="5" max="600"
            value={form.interval_seconds ?? ""} onChange={set("interval_seconds")}
            aria-label="Publish interval in seconds" />
        </Field>
        <Field label="Latitude" help={W.FIELD_HELP.lat}>
          <input className="tak-input" type="number" step="any"
            value={form.lat ?? ""} onChange={set("lat")} aria-label="Latitude" />
        </Field>
        <Field label="Longitude" help={W.FIELD_HELP.lon}>
          <input className="tak-input" type="number" step="any"
            value={form.lon ?? ""} onChange={set("lon")} aria-label="Longitude" />
        </Field>
      </div>

      <p className="tak-note">{W.TLS_NOTE}</p>
      {error && <p className="tak-error" role="alert">{error}</p>}

      <button type="submit" className="tak-btn tak-primary" disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </button>
    </form>
  );
}

/* A unit sits beside the input rather than only in the help line: "30" with no
   unit reads as minutes to about half the people who see it. */
function Field({ label, help, unit, children }) {
  return (
    <label className="tak-field">
      <span className="tak-label">{label}</span>
      {unit ? (
        <span className="tak-withunit">{children}<span className="tak-unit">{unit}</span></span>
      ) : children}
      <span className="tak-help">{help}</span>
    </label>
  );
}
